/**
 * Pure domain logic for Floor Entities discrimination (Ticket 02).
 * Separates IT Racks, Facility Units (Cooling, Power), and Architectural Elements.
 */

export type FloorEntityKind = 'rack' | 'cooling_unit' | 'power_unit' | 'architecture'

export type FloorEntityType =
  | 'server_rack'
  | 'network_rack'
  | 'open_frame'
  | 'crac'
  | 'inrow_cooler'
  | 'ups'
  | 'pdu_frame'
  | 'pillar'

export interface FloorEntityDefaults {
  kind: FloorEntityKind
  type: string
  label: string
  u_slots: number
  is_facility: boolean
  is_blocked: boolean
  width_m: number
  depth_m: number
  height_m: number
}

const FACILITY_TYPES = new Set(['crac', 'inrow_cooler', 'ups', 'pdu_frame', 'pillar'])
const ARCHITECTURE_TYPES = new Set(['pillar'])
const IT_RACK_TYPES = new Set(['server_rack', 'network_rack', 'open_frame'])

export function isFacilityType(type?: string | null): boolean {
  if (!type) return false
  return FACILITY_TYPES.has(type)
}

export function isArchitectureType(type?: string | null): boolean {
  if (!type) return false
  return ARCHITECTURE_TYPES.has(type)
}

export function isRackType(type?: string | null): boolean {
  if (!type) return false
  return IT_RACK_TYPES.has(type)
}

export function floorEntityKind(type?: string | null): FloorEntityKind {
  if (!type) return 'rack'
  if (type === 'crac' || type === 'inrow_cooler') return 'cooling_unit'
  if (type === 'ups' || type === 'pdu_frame') return 'power_unit'
  if (type === 'pillar') return 'architecture'
  return 'rack'
}

export function createFloorEntityDefaults(type: string): FloorEntityDefaults {
  switch (type) {
    case 'server_rack':
      return {
        kind: 'rack',
        type,
        label: 'Стойка',
        u_slots: 42,
        is_facility: false,
        is_blocked: false,
        width_m: 0.6,
        depth_m: 1.0,
        height_m: 2.0,
      }
    case 'network_rack':
      return {
        kind: 'rack',
        type,
        label: 'Сетевая стойка',
        u_slots: 42,
        is_facility: false,
        is_blocked: false,
        width_m: 0.8,
        depth_m: 1.0,
        height_m: 2.0,
      }
    case 'open_frame':
      return {
        kind: 'rack',
        type,
        label: 'Открытая рама',
        u_slots: 42,
        is_facility: false,
        is_blocked: false,
        width_m: 0.6,
        depth_m: 1.0,
        height_m: 2.0,
      }
    case 'crac':
      return {
        kind: 'cooling_unit',
        type,
        label: 'CRAC',
        u_slots: 0,
        is_facility: true,
        is_blocked: false,
        width_m: 1.2,
        depth_m: 1.0,
        height_m: 2.0,
      }
    case 'inrow_cooler':
      return {
        kind: 'cooling_unit',
        type,
        label: 'In-Row',
        u_slots: 0,
        is_facility: true,
        is_blocked: false,
        width_m: 0.3,
        depth_m: 1.0,
        height_m: 2.0,
      }
    case 'ups':
      return {
        kind: 'power_unit',
        type,
        label: 'ИБП',
        u_slots: 0,
        is_facility: true,
        is_blocked: false,
        width_m: 0.8,
        depth_m: 1.0,
        height_m: 2.0,
      }
    case 'pdu_frame':
      return {
        kind: 'power_unit',
        type,
        label: 'PDU',
        u_slots: 0,
        is_facility: true,
        is_blocked: false,
        width_m: 0.6,
        depth_m: 1.0,
        height_m: 2.0,
      }
    case 'pillar':
      return {
        kind: 'architecture',
        type,
        label: 'Колонна',
        u_slots: 0,
        is_facility: true,
        is_blocked: true,
        width_m: 0.6,
        depth_m: 0.6,
        height_m: 3.0,
      }
    default:
      return {
        kind: 'rack',
        type,
        label: 'Стойка',
        u_slots: 42,
        is_facility: false,
        is_blocked: false,
        width_m: 0.6,
        depth_m: 1.0,
        height_m: 2.0,
      }
  }
}

export interface HallCapacitySummary {
  totalItRacks: number
  totalUSlots: number
  facilityCount: number
  pillarCount: number
}

/**
 * Calculates useful IT capacity without contamination from facility units or pillars.
 */
export function calculateHallItCapacity(
  entities: Array<{
    u_slots?: number | null
    uCount?: number | null
    is_facility?: boolean
    isFacility?: boolean
    is_blocked?: boolean
    type?: string
    rackType?: string
  }>
): HallCapacitySummary {
  let totalItRacks = 0
  let totalUSlots = 0
  let facilityCount = 0
  let pillarCount = 0

  for (const item of entities) {
    const type = item.type ?? item.rackType ?? 'server_rack'
    const isFacility = Boolean(item.is_facility || item.isFacility || isFacilityType(type))
    const isPillar = Boolean(item.is_blocked || isArchitectureType(type))

    if (isPillar) {
      pillarCount++
    }

    if (isFacility) {
      facilityCount++
    } else {
      totalItRacks++
      const slots = item.u_slots ?? item.uCount ?? 42
      totalUSlots += slots
    }
  }

  return {
    totalItRacks,
    totalUSlots,
    facilityCount,
    pillarCount,
  }
}
